const STORAGE_KEYS = {
  tenantDomain: 'tenant_domain',
  logoutRedirectUri: 'logout_redirect_uri',
  tenantData: 'tenant_data',
};

function isBrowser() {
  return typeof window !== 'undefined';
}

function normalizeDomain(domain) {
  if (!domain || typeof domain !== 'string') return null;

  let d = domain.trim();
  if (!d) return null;

  // Accept either a plain hostname or a full URL.
  if (/^https?:\/\//i.test(d)) {
    try {
      d = new URL(d).hostname;
    } catch {
      return null;
    }
  }

  d = d.toLowerCase();

  // Basic hostname sanity.
  if (d.includes('/') || d.includes('\\') || d.includes('@')) return null;
  if (!d.includes('.')) return null;

  // Env-prefixed domains (dev/test/staging) must never have www.
  // Strip it if it was incorrectly cached in localStorage before this fix:
  //   www.dev.tippelstreet.com  →  dev.tippelstreet.com
  if (/^www\.(dev|test|staging)\./.test(d)) {
    d = d.slice(4); // remove 'www.'
  }

  // Production domains are canonicalized to include www.
  // Env-prefixed domains are already correct after the strip above.
  const hasEnvPrefix = /^(dev|test|staging)\./.test(d);
  if (!d.startsWith('www.') && !hasEnvPrefix) {
    d = `www.${d}`;
  }

  return d;
}

export function setTenantDomain(domain) {
  if (!isBrowser()) return;
  const normalized = normalizeDomain(domain);
  if (!normalized) return;
  localStorage.setItem(STORAGE_KEYS.tenantDomain, normalized);
}

export function getTenantDomain() {
  if (!isBrowser()) return null;

  const direct = localStorage.getItem(STORAGE_KEYS.tenantDomain);
  const normalized = normalizeDomain(direct);
  if (normalized) return normalized;

  // Backward compatibility: infer from tenant_data if present.
  try {
    const tenantDataRaw = localStorage.getItem(STORAGE_KEYS.tenantData);
    if (tenantDataRaw) {
      const tenant = JSON.parse(tenantDataRaw);
      const fromTenantData = normalizeDomain(tenant?.domain);
      if (fromTenantData) return fromTenantData;
    }
  } catch {
    // ignore
  }

  // Direct entry support: infer the canonical tenant domain from the standalone
  // frontend's hostname. Two URL patterns are supported:
  //
  // Pattern A — live.{env?}.{domain} (live. prefix first):
  //   live.tippelstreet.com         → tippelstreet.com          → www.tippelstreet.com
  //   live.dev.tippelstreet.com     → dev.tippelstreet.com      (no www, env prefix)
  //   live.test.tippelstreet.com    → test.tippelstreet.com     (no www, env prefix)
  //   live.tippelstreet.com.test    → dev.tippelstreet.com.test (no www, local Herd dev)
  //
  // Pattern B — {env}.live.{domain} (env prefix first, Traefik/Kong convention):
  //   dev.live.tippelstreet.com     → dev.tippelstreet.com
  //   test.live.tippelstreet.com    → test.tippelstreet.com
  //
  // The .test TLD is Herd's local-only suffix. Local tenant domains follow the
  // dev. convention: live.X.test → dev.X.test → www.dev.X.test.
  try {
    const host = window.location.hostname?.toLowerCase() || '';

  // Pattern B: {env}.live.{domain} → {env}.{domain}
    // Must be checked first because 'dev.live.x' does NOT start with 'live.'.
    // Returns the tenant domain (e.g. dev.tippelstreet.com) used as ?domain= in
    // /tenant/resolve. The .admin. variant is the gateway subdomain, not the tenant
    // domain, and causes a "Tenant not found" error on the mobile live player.
    const envLiveMatch = host.match(/^(dev|test|staging)\.live\.(.+)$/);
    if (envLiveMatch) {
      const [, envPrefix, baseDomain] = envLiveMatch;
      return normalizeDomain(`${envPrefix}.${baseDomain}`);
    }

    // Pattern A: live.{remainder} → {remainder}
    if (host.startsWith('live.')) {
      let domain = host.slice('live.'.length);

      // For .test TLD (local Herd only): prefix with dev. when no environment
      // prefix is already present. live.X.test → dev.X.test.
      if (domain.endsWith('.test') && !/^(dev|test|staging)\./.test(domain)) {
        domain = 'dev.' + domain;
      }

      return normalizeDomain(domain);
    }
  } catch {
    // ignore
  }

  return null;
}

export function setLogoutRedirectUri(uri) {
  if (!isBrowser()) return;
  if (!uri || typeof uri !== 'string') return;

  const value = uri.trim();
  if (!value) return;

  // Keep validation minimal: accept absolute http(s) urls only.
  if (!/^https?:\/\//i.test(value)) return;

  localStorage.setItem(STORAGE_KEYS.logoutRedirectUri, value);
}

export function getLogoutRedirectUri() {
  if (!isBrowser()) return null;
  const uri = localStorage.getItem(STORAGE_KEYS.logoutRedirectUri);
  return uri && typeof uri === 'string' && uri.trim() ? uri.trim() : null;
}

export function clearTenantContext() {
  if (!isBrowser()) return;
  localStorage.removeItem(STORAGE_KEYS.tenantDomain);
  localStorage.removeItem(STORAGE_KEYS.logoutRedirectUri);
}

export function setTenantData(tenant) {
  if (!isBrowser()) return;
  if (!tenant || typeof tenant !== 'object') return;
  localStorage.setItem(STORAGE_KEYS.tenantData, JSON.stringify(tenant));
  if (tenant.domain) setTenantDomain(tenant.domain);
}

export function getTenantData() {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.tenantData);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearTenantData() {
  if (!isBrowser()) return;
  localStorage.removeItem(STORAGE_KEYS.tenantData);
}

/**
 * Display-friendly variant of `getTenantDomain()`.
 *
 * The canonical tenant domain is normalised to include `www.` for
 * production and to keep env-prefixes (`dev.`/`test.`/`staging.`) in
 * place. Headers and titles look noisier with the `www.` prefix, so
 * for human-readable surfaces we strip it off:
 *
 *   www.tippelstreet.com       →  tippelstreet.com
 *   dev.tippelstreet.com       →  dev.tippelstreet.com   (env prefix kept)
 *   dev.tippelstreet.com.test  →  dev.tippelstreet.com.test
 *
 * Returns `null` when no tenant context is available, so callers can
 * fall back to a hard-coded brand name.
 */
export function getTenantDisplayDomain() {
  const domain = getTenantDomain();
  if (!domain) return null;
  return domain.startsWith('www.') ? domain.slice(4) : domain;
}

/**
 * Build the canonical homepage URL for the current tenant.
 *
 * Uses the full normalised domain (with `www.` for production, env
 * prefix for non-prod) so the link resolves to the same canonical
 * hostname configured at the gateway side. On Herd `.test` TLD this
 * still works because Herd serves HTTPS for `.test` automatically.
 *
 * Returns `null` when no tenant context is available so callers can
 * hide the affordance instead of linking to a broken URL.
 */
export function getTenantHomepageUrl() {
  const domain = getTenantDomain();
  if (!domain) return null;
  return `https://${domain}`;
}
